import DownloadIcon from "@assets/images/down-arrow.svg";
import { Typography } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { AppConfig_appConfig_appConfigs_edges_node } from "@saleor/appConfiguration/types/AppConfig";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colActions: {
        width: "20%"
      },
      colEmail: {
        width: "30%"
      },
      colTypeLevel: {
        width: "30%"
      },
      colVersion: {
        width: 300
      }
    },
    colActions: {
      paddingRight: theme.spacing(),
      textAlign: "center"
    },
    colProducts: {
      textAlign: "center"
    },
    colSubcategories: {
      textAlign: "center"
    },
    colVersion: {
      paddingLeft: 0
    },
    collectionTbl: {
      "@media (max-width: 767px)": {
        width: "800px"
      }
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "AppConfigDetailList" }
);

interface AppConfigDetailListProps extends ListProps {
  appConfig: AppConfig_appConfig_appConfigs_edges_node[];
}

const numberOfColumns = 4;

const AppConfigDetailList: React.FC<AppConfigDetailListProps> = props => {
  const {
    appConfig,
    settings,
    disabled,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings
  } = props;

  const classes = useStyles(props);

  return (
    <ResponsiveTable className={classes.collectionTbl}>
      <TableHead>
        <TableCellHeader
          direction={undefined}
          arrowPosition="right"
          className={classes.colVersion}
        >
          <FormattedMessage defaultMessage="File Version" />
        </TableCellHeader>
        <TableCellHeader className={classes.colEmail}>
          <FormattedMessage
            defaultMessage="Date Updated"
            description="Date Updated"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colEmail}>
          <FormattedMessage
            defaultMessage="Updated by"
            description="Updated by"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colActions}></TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>

      <TableBody>
        {renderCollection(
          appConfig,
          (appConfig, index) => {
            const download = (url, filename) => {
              fetch(url)
                .then(response => response.blob())
                .then(blob => {
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.download = filename;
                  link.click();
                })
                .catch(console.error);
            };
            return (
              <TableRow
                className={classes.tableRow}
                hover={!!appConfig}
                key={appConfig ? appConfig.id : "skeleton"}
                data-test="id"
                data-test-id={maybe(() => appConfig.id)}
              >
                <TableCell
                  className={classes.colVersion}
                  data-test="version"
                  id={`fileVersionRow${index}`}
                >
                  {appConfig && appConfig.version >= 0 ? (
                    `v${appConfig.version}`
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colEmail}
                  data-test="dateUpdated"
                  id={`dateUpdatedRow${index}`}
                >
                  {appConfig && appConfig.createdAt ? (
                    <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                      {appConfig?.createdAt}
                    </Moment>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell
                  className={classes.colEmail}
                  data-test="updatedBy"
                  id={`updatedByRow${index}`}
                >
                  {appConfig && appConfig.createdBy ? (
                    <Typography>{`${appConfig.createdBy?.firstName} ${appConfig.createdBy?.lastName}`}</Typography>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colActions}>
                  <IconButton
                    id={`downloadBtnRow${index}`}
                    color="primary"
                    onClick={() => download(`${appConfig.url}`, "test.json")}
                  >
                    <img src={DownloadIcon} style={{ width: "15px" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No application configurations found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

AppConfigDetailList.displayName = "AppConfigDetailList";
export default AppConfigDetailList;
